/**
 * Auth + profile-builder shared types.
 *
 * The auth surface is intentionally narrow so a Supabase-backed
 * implementation can drop in later without changing component contracts:
 *   - `signUp` / `logIn` / `logOut` are the only mutators
 *   - User shape is the minimum any backend would expose
 *   - Errors come back as a string the UI can render directly
 */

export interface HottrUser {
  /** Stable id from the auth backend. `mock_<uuid>` in mock mode. */
  id: string;
  email: string;
  /** Convenience pointer to the user's app-profile handle once chosen. */
  handle?: string;
  createdAt: string;
}

export interface AuthResult {
  ok: boolean;
  /** User-renderable error string. */
  error?: string;
  /**
   * Set by `signUp` when the backend created the account but the user
   * must confirm their email before a session is issued. The UI should
   * show a "check your email" state instead of treating this as success.
   */
  needsConfirmation?: boolean;
}

/**
 * Looking-for tonight — energy-based, not identity-based. Single-select.
 * Matches canon §9 HEAT cold-start "Tonight Mode" prompt.
 */
export type TonightWant =
  | 'right_now'
  | 'good_company'
  | 'out_for_it'
  | 'just_looking';

/**
 * Visibility preference — who the user wants to *see*, multi-select.
 * Never describes who the user IS. Same shape used on HOTTR.vip.
 */
export type VisibilityPreference = 'men' | 'women' | 'trans';

/**
 * Physical / behavioural stats the user can volunteer. All optional past
 * the canon-required minimum (height + body + position + cockSize is the
 * sweet spot for cold-start HEAT). Same shape as `MockProfile` so the
 * builder can write straight into the profile store later.
 */
export type Body =
  | 'lean'
  | 'slim'
  | 'average'
  | 'athletic'
  | 'fit'
  | 'muscular'
  | 'stocky'
  | 'large'
  | 'dad';

export type Position = 'top' | 'vers top' | 'vers' | 'vers bottom' | 'bottom' | 'side';

export type Tribe =
  | 'jock'
  | 'otter'
  | 'twink'
  | 'bear'
  | 'cub'
  | 'daddy'
  | 'leather'
  | 'pup'
  | 'geek';

export type CockSize = 'small' | 'average' | 'large' | 'xlarge' | 'huge';

export interface ProfileDraft {
  /** Lowercase alphanumeric + underscore. Set on builder step 1. */
  handle: string;
  /** Display name — shown on profile + cards. */
  displayName: string;
  /** True only when the user has explicitly confirmed 18+. */
  ageConfirmed: boolean;
  /** Numeric age — separate from the 18+ confirmation. */
  age: number | null;
  /** Suburb / city string. Null until set. */
  location: string | null;
  /**
   * True if the user dismissed the location prompt. Lets us still treat the
   * profile as complete without forcing a city to be picked.
   */
  locationPermissionDismissed: boolean;
  /** Editorial mood line — lowercase, short. */
  mood: string;
  /** What they want tonight. Single-select. */
  lookingForTonight: TonightWant | null;
  /** Who they want to see tonight. Multi-select preference, NOT identity. */
  wantsToSee: VisibilityPreference[];

  /* ───────── What you look like — feeds HEAT cold-start ranking ───────── */
  height: string;
  weight: string;
  body: Body | null;
  position: Position | null;
  cockSize: CockSize | null;

  /* ───────── What you like — feeds HEAT cold-start surfacing ───────── */
  /** 1–3 lowercase tags. */
  interests: string[];
  /** Tribe affiliation, optional. */
  tribe: Tribe | null;

  /**
   * Photos uploaded by the user. Stored as data URLs in the mock backend
   * (downscaled before persistence so localStorage doesn't blow out). In
   * production these become Supabase Storage references.
   */
  photos: string[];

  /** ISO timestamp set when the builder is marked complete. */
  completedAt: string | null;
}

export const EMPTY_DRAFT: ProfileDraft = {
  handle: '',
  displayName: '',
  ageConfirmed: false,
  age: null,
  location: null,
  locationPermissionDismissed: false,
  mood: '',
  lookingForTonight: null,
  wantsToSee: [],
  height: '',
  weight: '',
  body: null,
  position: null,
  cockSize: null,
  interests: [],
  tribe: null,
  photos: [],
  completedAt: null,
};

/**
 * Completion contract — exposed as a pure helper so route guards and UI
 * use the same rule. Keep this in sync with the brief:
 *
 *   handle + displayName + ageConfirmed + lookingForTonight + wantsToSee
 *   + (location || locationPermissionDismissed)
 *   + at least 1 photo
 *   + at least 1 interest
 *
 * Stats (height / body / position / cockSize) are optional — they
 * enrich HEAT but aren't a hard gate.
 */
export const TONIGHT_WANTS: readonly TonightWant[] = [
  'right_now',
  'good_company',
  'out_for_it',
  'just_looking',
];

export const VISIBILITY_PREFS: readonly VisibilityPreference[] = ['men', 'women', 'trans'];

export function isTonightWant(value: unknown): value is TonightWant {
  return typeof value === 'string' && (TONIGHT_WANTS as readonly string[]).includes(value);
}

export function parseWantsToSee(values: readonly string[] | null | undefined): VisibilityPreference[] {
  const seen = new Set<VisibilityPreference>();
  for (const raw of values ?? []) {
    const key = raw.trim().toLowerCase();
    if ((VISIBILITY_PREFS as readonly string[]).includes(key)) {
      seen.add(key as VisibilityPreference);
    }
  }
  return [...seen];
}

/** Columns we read when hydrating a returning user's local draft. */
export type AppProfileDraftRow = {
  handle?: string | null;
  display_name?: string | null;
  looking_for_tonight?: string | null;
  wants_to_see?: string[] | null;
  room?: string | null;
  photo_url?: string | null;
  additional_photos?: string[] | null;
  interests?: string[] | null;
};

export function draftFromAppProfile(
  row: AppProfileDraftRow,
  now = new Date().toISOString(),
): ProfileDraft {
  const room = typeof row.room === 'string' ? row.room.trim() : '';
  const photos = [row.photo_url, ...(row.additional_photos ?? [])].filter(
    (url): url is string => typeof url === 'string' && url.trim().length > 0,
  );
  return {
    ...EMPTY_DRAFT,
    handle: typeof row.handle === 'string' ? row.handle.trim() : '',
    displayName: typeof row.display_name === 'string' ? row.display_name.trim() : '',
    ageConfirmed: true,
    lookingForTonight: isTonightWant(row.looking_for_tonight) ? row.looking_for_tonight : 'just_looking',
    wantsToSee: parseWantsToSee(row.wants_to_see),
    location: room || null,
    locationPermissionDismissed: !room,
    photos,
    interests: (row.interests ?? []).filter((i) => typeof i === 'string' && i.trim().length > 0),
    completedAt: now,
  };
}

export const isDraftComplete = (d: ProfileDraft | null): boolean => {
  if (!d) return false;
  if (typeof d.completedAt === 'string' && d.completedAt.trim().length > 0) return true;
  return (
    d.handle.trim().length > 0 &&
    d.displayName.trim().length > 0 &&
    d.ageConfirmed === true &&
    d.lookingForTonight !== null &&
    d.wantsToSee.length > 0 &&
    (Boolean(d.location) || d.locationPermissionDismissed) &&
    d.photos.length >= 1 &&
    d.interests.filter((i) => i.trim().length > 0).length >= 1
  );
};
